import { BRAND_COLORS } from './brand';
import {
  COMFORT_DENSITY,
  FOUNDATION_COLOURS,
  PRODUCTIVE_DENSITY,
  SEMANTIC_COLOUR_TOKENS,
  TYPOGRAPHY_ROLES,
} from './design-tokens/source';

export const SPACING_SCALE = {
  1: '4px',
  2: '8px',
  3: '12px',
  4: '16px',
  5: '20px',
  6: '24px',
  8: '32px',
  10: '40px',
  12: '48px',
  16: '64px',
} as const;

export const RADIUS_SCALE = {
  sm: '4px',
  DEFAULT: '8px',
  lg: '8px',
  xl: '12px',
  '2xl': '12px',
  '3xl': '16px',
  full: '9999px',
  card: PRODUCTIVE_DENSITY['radius-surface'],
  control: PRODUCTIVE_DENSITY['radius-control'],
  overlay: PRODUCTIVE_DENSITY['radius-overlay'],
  badge: PRODUCTIVE_DENSITY['radius-badge'],
  'card-comfort': COMFORT_DENSITY['radius-surface'],
  'control-comfort': COMFORT_DENSITY['radius-control'],
} as const;

export const SHADOW_SCALE = {
  sm: '0 1px 2px rgba(15, 23, 42, 0.05)',
  default: '0 4px 6px rgba(15, 23, 42, 0.07)',
  md: '0 6px 12px rgba(15, 23, 42, 0.1)',
  card: COMFORT_DENSITY['elevation-surface'],
  'card-comfort': COMFORT_DENSITY['elevation-surface'],
  'card-hover': '0 20px 40px rgba(15, 23, 42, 0.1)',
  xl: '0 20px 40px rgba(15, 23, 42, 0.12)',
} as const;

export const MOTION_TOKENS = {
  easing: {
    productive: 'ease-out',
  },
  duration: {
    fast: '150ms',
    normal: '200ms',
    slow: '300ms',
    deliberate: '500ms',
  },
  transitionProperty: {
    interactive: 'background-color, border-color, color, box-shadow, transform, opacity',
  },
} as const;

export const SHELL_TOKENS = {
  page: {
    background: BRAND_COLORS.slate50,
    backgroundSoft: BRAND_COLORS.fog,
    sidebar: BRAND_COLORS.inkDark,
    topbar: BRAND_COLORS.white,
  },
  surface: {
    base: BRAND_COLORS.white,
    soft: BRAND_COLORS.mist,
    inverse: BRAND_COLORS.inkDark,
  },
  text: {
    heading: BRAND_COLORS.ink,
    body: BRAND_COLORS.slate700,
    muted: SEMANTIC_COLOUR_TOKENS.light['text.muted'],
    inverse: BRAND_COLORS.white,
  },
  border: {
    subtle: '#E2E8F0',
    default: BRAND_COLORS.slate300,
  },
  focus: {
    ring: BRAND_COLORS.green,
    ringAccessible: BRAND_COLORS.greenAccessible,
    ringShadow: 'rgba(150, 169, 152, 0.2)',
  },
} as const;

/** Compatibility projection of the canonical roles; all legacy names remain exported. */
export const TYPOGRAPHY_TOKENS = Object.fromEntries(
  Object.entries(TYPOGRAPHY_ROLES).map(([name, role]) => [
    name,
    {
      fontSize: role.fontSize,
      lineHeight: String(role.lineHeight),
      letterSpacing: role.letterSpacing,
      fontFamily: role.family === 'display' ? 'serif' : 'sans',
      fontWeight: String(role.fontWeight),
    },
  ]),
) as {
  [Role in keyof typeof TYPOGRAPHY_ROLES]: {
    fontSize: (typeof TYPOGRAPHY_ROLES)[Role]['fontSize'];
    lineHeight: string;
    letterSpacing: string;
    fontFamily: 'serif' | 'sans';
    fontWeight: string;
  };
};

export const ACTION_TOKENS = {
  primary: FOUNDATION_COLOURS['action.primary'],
  primaryHover: FOUNDATION_COLOURS['action.primary-hover'],
  primaryForeground: FOUNDATION_COLOURS['action.primary-foreground'],
} as const;

export const CHART_TOKENS = {
  categorical: {
    primary: BRAND_COLORS.greenAccessible,
    secondary: BRAND_COLORS.ink,
    tertiary: BRAND_COLORS.deep,
    quaternary: '#4F6D5A',
    quinary: '#7C8EA3',
    senary: '#A7B4C3',
  },
  semantic: {
    success: BRAND_COLORS.success,
    warning: BRAND_COLORS.warning,
    danger: BRAND_COLORS.error,
    info: BRAND_COLORS.info,
  },
  grid: BRAND_COLORS.slate100,
  axis: BRAND_COLORS.slate500,
} as const;

export type TypographyTokenName = keyof typeof TYPOGRAPHY_TOKENS;
